import { Card, CardHeader } from "react-bootstrap";
import { Content } from "./Controls/Content";
import { Row, Col } from "react-bootstrap";
import { CheckCircleFill, XCircleFill } from "react-bootstrap-icons";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "./Data/ApiStripe";
import { PaymentIntent } from "@stripe/stripe-js";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { Button } from "./Controls/Button";

export interface PaymentStatusProps {
    pmtIntentKey:          string;
    pmtIntentClientSecret: string;
    redirectStatus:        string;
    returnTo:              string;
    returnLabel:           string;
    source?:               string;
}

export function status( status: PaymentIntent.Status ) {
    switch( status ) {
        case "canceled":                return "Canceled";
        case "processing":              return "Processing";
        case "requires_action":         return "Requires Action";
        case "requires_capture":        return "Hold Successful";
        case "requires_confirmation":   return "Requires Confirmation";
        case "requires_payment_method": return "Requires Payment Method";
        case "succeeded":               return "Succeeded";
        default:                        return "Unrecognized Payment Status";
    }
}

export function description( status: PaymentIntent.Status, source?: string ) {
    switch( status ) {
        case "canceled":                return ["Canceled"];
        case "processing":              return ["Your payment is processing with your bank and will take several days to settle."];
        case "requires_action":         return ["You will be emailed instructions for additional information needed to verify your bank account.","Your parking subscription has been submitted for approval."];
        case "requires_capture":        return ["We have placed a hold on your payment method.",
                                                source === "hourly" 
                                                    ? "Payment will be captured once your session is complete."
                                                    : "If a staff member approves your application the payment will go through."];
        case "requires_confirmation":   return ["Requires Confirmation"];
        case "requires_payment_method": return ["Requires Payment Method"];
        case "succeeded":               return ["We have successfully received your payment!",
                                                "You will receive an email confirmation shortly."];
        default:                        return ["Unrecognized Payment Status"];
    }
}

function isSuccessStatus(status: PaymentIntent.Status): boolean {
    return ["succeeded", "processing", "requires_capture", "requires_action"].includes(status);
}

export function PaymentStatus( { pmtIntentClientSecret, returnTo, returnLabel, source }: PaymentStatusProps ) {

    const stripe = useStripe();
    const nav = useNavigate();
    const [msg, setMsg] = useState<string>();
    const [desc, setDesc] = useState<string[]>( [] );
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [ auth, setAuth ] = useAtom( authAtom );
    const [isLoaded, setIsLoaded] = useState(false);

    const load = async () => {
        if( !stripe ) {
            return;
        }

        // retrieve the paymentintent
        const { paymentIntent } = await stripe.retrievePaymentIntent( pmtIntentClientSecret )

        if( !paymentIntent ) {         
            return;
        }


        // Inspect the PaymentIntent `status` to indicate the status of the payment
        // to your customer.
        // Some payment methods will [immediately succeed or fail][0] upon
        // confirmation, while others will first enter a `processing` state.
        // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
        
        setMsg(status(paymentIntent.status));
        setDesc(description(paymentIntent.status, source));
        setIsSuccess(isSuccessStatus(paymentIntent.status));
        setIsLoaded(true);
    }

    useEffect( () => {
        load();
    }, [stripe] );

    return <Content fluid>
        <Row className="justify-content-center mt-5">
            <Col md="6">
                {isLoaded && (
                    <Card>
                        <CardHeader className="text-center">
                            <div className="d-flex justify-content-center mb-4">
                                {isSuccess ? 
                                    <CheckCircleFill color="#FF4DB8" size={64} /> :
                                    <XCircleFill color="danger" size={64} />
                                }
                            </div>
                            <h3>{msg}</h3>
                        </CardHeader>
                        <Card.Body className="text-center pt-4">
                            {desc.map((d, i) => (
                                <p key={i} className="mb-2">{d}</p>
                            ))}
                            {auth.isLoggedIn && (
                                <Button className="mt-4" onClick={() => nav(returnTo)}>
                                    {returnLabel}
                                </Button>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </Col>
        </Row>
    </Content>;
}

export function UserPaidPage() {
    //?payment_intent=pi_3QOsYpRR5LM9CxtV1Hv8VGP5&payment_intent_client_secret=pi_3QOsYpRR5LM9CxtV1Hv8VGP5_secret_lOuQxrDKeH6lkDQXFoMDfrg15&redirect_status=succeeded
    const [searchParams] = useSearchParams();
    const pmtIntentKey          = searchParams.get( "payment_intent"               )!;
    const pmtIntentClientSecret = searchParams.get( "payment_intent_client_secret" )!;
    const redirectStatus        = searchParams.get( "redirect_status"              )!;
    const returnTo = searchParams.get("returnTo") ?? "/user/profile"; 
    const returnLabel = searchParams.get("returnLabel") ?? "Go to Profile"; 
    const source = searchParams.get("source") ?? undefined;

    return <Elements stripe={stripePromise}>
        <PaymentStatus pmtIntentClientSecret={pmtIntentClientSecret}
                       pmtIntentKey={pmtIntentKey}
                       redirectStatus={redirectStatus}
                       returnTo={returnTo} 
                       returnLabel={returnLabel}
                       source={source}/>
    </Elements>
}